import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'
import { useAnchoredPopover } from '@/shared/ui/popover'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  /** Строка для пустого значения; без неё пустой выбор недоступен. */
  placeholder?: string
  disabled?: boolean
  id?: string
  className?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

// Свой listbox вместо нативного <select>: список опций у нативного рисует
// браузер, и он не поддаётся ни токенам, ни шрифту — в тёмном UI это чужеродная
// системная панель. Здесь список наш, поэтому опции выглядят как остальное
// приложение. Раскладка (ширина) достаётся обёртке, контрол тянется по ней.
export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  id,
  className,
  ...aria
}: SelectProps) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  const {
    anchorRef: triggerRef,
    popoverRef: listRef,
    popoverProps,
  } = useAnchoredPopover<HTMLButtonElement, HTMLDivElement>({ open, matchWidth: true })

  const items: SelectOption[] = placeholder
    ? [{ value: '', label: placeholder }, ...options]
    : options
  const selected = items.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (
        !rootRef.current?.contains(e.target as Node) &&
        !listRef.current?.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open, listRef])

  // Открываем на текущем значении: стрелками пользователь идёт от того, что
  // выбрано, а не от начала списка.
  function openList() {
    if (disabled) return
    const current = items.findIndex((o) => o.value === value)
    setActiveIndex(current >= 0 ? current : 0)
    setOpen(true)
  }

  function choose(option: SelectOption) {
    if (option.disabled) return
    onChange(option.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  function step(delta: number) {
    const next = items
      .map((o, i) => ({ o, i }))
      .filter(({ o }) => !o.disabled)
      .map(({ i }) => i)
    if (next.length === 0) return
    const pos = next.indexOf(activeIndex)
    const target = next[Math.min(Math.max(pos + delta, 0), next.length - 1)] ?? next[0]
    setActiveIndex(target as number)
  }

  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) openList()
        else step(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (!open) openList()
        else step(-1)
        break
      case 'Home':
        if (open) {
          e.preventDefault()
          setActiveIndex(0)
        }
        break
      case 'End':
        if (open) {
          e.preventDefault()
          setActiveIndex(items.length - 1)
        }
        break
      case 'Enter':
      case ' ': {
        e.preventDefault()
        if (!open) {
          openList()
          return
        }
        const option = items[activeIndex]
        if (option) choose(option)
        break
      }
      case 'Escape':
        if (open) {
          e.preventDefault()
          setOpen(false)
        }
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        {...aria}
        className="flex w-full items-center justify-between gap-2 rounded-control border border-border-default bg-bg-sunken px-4 pt-3 pb-[14px] text-left text-text-primary transition-colors hover:border-border-strong focus:border-border-accent focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={cn('truncate', selected?.value ? 'text-text-primary' : 'text-text-tertiary')}
        >
          {selected?.label ?? placeholder ?? ''}
        </span>
        <Icon name="chevron-down" size={18} aria-hidden className="shrink-0 text-text-tertiary" />
      </button>
      {open && (
        <div
          ref={listRef}
          {...popoverProps}
          role="listbox"
          id={listId}
          aria-label={aria['aria-label']}
          className="m-0 max-h-64 overflow-y-auto rounded-control border border-border-default bg-bg-elevated py-1 shadow-lg shadow-black/40"
        >
          {items.map((option, index) => (
            <button
              key={option.value || 'empty'}
              type="button"
              role="option"
              aria-selected={option.value === value}
              disabled={option.disabled}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option)}
              className={cn(
                'flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors disabled:opacity-40',
                index === activeIndex ? 'bg-bg-raised text-text-primary' : 'text-text-secondary',
                option.value === '' && 'text-text-tertiary',
              )}
            >
              <span className="truncate">{option.label}</span>
              {option.value === value && (
                <Icon name="check" size={14} aria-hidden className="shrink-0 text-accent" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
